/**
 * Create Customer from Xero Quote — Auto-fill Card
 *
 * Drop-in card for the New-Customer form. Admin searches Xero for an existing
 * quote (by quote number or customer name), picks one, and we pull the
 * contact details + classify the line items (hardware / qty / cost) into the
 * form fields. Everything is presented for the admin to edit / confirm before
 * saving — nothing is written to the CRM until they hit "Save Customer".
 *
 * Props:
 *  - form: react-hook-form instance
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { FileText, Loader2, Search, Sparkles, CheckCircle2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { xeroAPI } from '../lib/api';

const fmtGBP = (v) => `£${(parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export const XeroQuoteImportCard = ({ form }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null); // null = not searched yet
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [preview, setPreview] = useState(null); // { quote, line_items, draft }
  const [editDraft, setEditDraft] = useState(null); // editable copy of the draft
  const [appliedQuote, setAppliedQuote] = useState(null);

  const upd = (k, v) => setEditDraft((d) => ({ ...(d || {}), [k]: v }));

  const runSearch = async (e) => {
    e?.preventDefault?.();
    setSearching(true);
    setResults(null);
    setPreview(null);
    try {
      const res = await xeroAPI.searchQuotes(query.trim());
      setResults(res.data?.quotes || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not search Xero quotes');
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const loadQuote = async (quoteId) => {
    setLoadingDraft(true);
    try {
      const res = await xeroAPI.quoteDraftCustomer(quoteId);
      setPreview(res.data);
      setEditDraft(res.data?.draft || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read this Xero quote');
    } finally {
      setLoadingDraft(false);
    }
  };

  const applyToForm = () => {
    const draft = editDraft || preview?.draft || {};
    const setIfEmpty = (name, value) => {
      if (value === null || value === undefined || value === '' || value === 0) return false;
      const cur = form.getValues(name);
      const empty = cur === '' || cur === null || cur === undefined || cur === 0 || cur === '0';
      if (empty) { form.setValue(name, value, { shouldDirty: true }); return true; }
      return false;
    };

    let count = 0;
    [
      'first_name', 'last_name', 'email', 'phone', 'address', 'city', 'state',
      'postcode', 'system_total_cost', 'system_size',
      'panel_manufacturer', 'panel_model', 'panel_quantity',
      'inverter_manufacturer', 'inverter_model', 'inverter_quantity',
      'battery_manufacturer', 'battery_model', 'battery_quantity',
      'ev_charger_manufacturer', 'ev_charger_model', 'ev_charger_quantity',
    ].forEach((k) => { if (setIfEmpty(k, draft[k])) count += 1; });

    // Additional works identified on the quote → customer notes.
    if (draft.additional_extras) {
      const qn = preview?.quote?.quote_number ? ` (Xero quote ${preview.quote.quote_number})` : '';
      const block = `Additional works${qn}:\n${draft.additional_extras}`;
      const cur = form.getValues('notes') || '';
      if (!cur.includes(block)) {
        form.setValue('notes', cur ? `${cur}\n\n${block}` : block, { shouldDirty: true });
        count += 1;
      }
    }

    // System-type checkboxes — OR in whatever the quote identified, but only if
    // the admin hasn't already picked a type.
    const typeKeys = ['system_type_solar', 'system_type_solar_battery', 'system_type_battery_only', 'system_type_ev_charger', 'system_type_electrical'];
    const noneSelected = !typeKeys.some((k) => form.getValues(k));
    if (noneSelected) {
      typeKeys.forEach((k) => { if (draft[k]) form.setValue(k, true, { shouldDirty: true }); });
    }

    if (preview?.quote?.quote_id) {
      form.setValue('source_xero_quote_id', preview.quote.quote_id, { shouldDirty: true });
    }

    setAppliedQuote(preview?.quote || null);
    setOpen(false);
    toast.success(`Quote ${preview?.quote?.quote_number || ''} imported — ${count} fields filled. Review & edit below, then Save.`, { duration: 6000 });
  };

  return (
    <>
      <Card className="border-sky-200 bg-sky-50/40" data-testid="xero-quote-import-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold font-['Manrope'] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-sky-600" />
            Create from Xero Quote — Auto-fill
            <Badge variant="outline" className="ml-2 bg-white text-[10px] font-normal">Optional</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600">
            Already built this job as a quote in Xero? Search for it and we'll pull the
            customer's contact details and identify the hardware, quantities and cost into
            the form below. Empty fields only — anything you've typed is preserved.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => { setOpen(true); setResults(null); setPreview(null); }}
              className="bg-white"
              data-testid="xero-quote-import-open-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              {appliedQuote ? 'Import a different quote' : 'Search Xero quotes'}
            </Button>
            {appliedQuote && (
              <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-200" data-testid="xero-quote-applied-summary">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Imported {appliedQuote.quote_number} · {fmtGBP(appliedQuote.total)}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="xero-quote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-sky-600" /> Import from Xero Quote
            </DialogTitle>
            <DialogDescription>
              {preview
                ? 'Review the details pulled from this quote, then apply them to the customer form.'
                : 'Search by quote number (e.g. QU-0012) or customer name.'}
            </DialogDescription>
          </DialogHeader>

          {!preview && (
            <>
              <form onSubmit={runSearch} className="flex gap-2">
                <Input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Quote number or customer name"
                  data-testid="xero-quote-search-input"
                />
                <Button type="submit" disabled={searching} data-testid="xero-quote-search-btn">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </form>

              <div className="mt-3 space-y-2">
                {searching && (
                  <div className="flex items-center justify-center py-8 text-slate-500">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Searching Xero…
                  </div>
                )}
                {results && results.length === 0 && !searching && (
                  <p className="text-sm text-slate-500 py-6 text-center" data-testid="xero-quote-no-results">
                    No quotes found. Try the quote number or a different name.
                  </p>
                )}
                {results && results.map((qt) => (
                  <button
                    key={qt.quote_id}
                    type="button"
                    onClick={() => loadQuote(qt.quote_id)}
                    disabled={loadingDraft}
                    className="w-full text-left rounded-lg border border-slate-200 bg-white hover:border-sky-400 hover:bg-sky-50/50 transition-colors p-3 flex items-center justify-between gap-3 disabled:opacity-60"
                    data-testid={`xero-quote-result-${qt.quote_id}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800">{qt.quote_number || '(no number)'}</span>
                        {qt.status && <Badge variant="outline" className="text-[10px]">{qt.status}</Badge>}
                        {qt.already_imported && <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200">Already imported</Badge>}
                      </div>
                      <div className="text-sm text-slate-600 truncate">{qt.contact_name || 'Unknown contact'}</div>
                      {qt.date && <div className="text-xs text-slate-400">{qt.date}</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-semibold text-slate-800">{fmtGBP(qt.total)}</div>
                      {loadingDraft && <Loader2 className="w-4 h-4 animate-spin text-sky-500 inline-block" />}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {preview && editDraft && (
            <div className="space-y-4" data-testid="xero-quote-preview">
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="text-sm text-sky-700 flex items-center gap-1 hover:underline"
                data-testid="xero-quote-back-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Back to results
              </button>

              <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-800">{preview.quote.quote_number}{preview.quote.status ? ` · ${preview.quote.status}` : ''}</div>
                  <div className="text-xs text-slate-500">Edit anything below before applying</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-slate-500">First name</Label>
                    <Input className="h-8 text-sm" value={editDraft.first_name || ''} onChange={(e) => upd('first_name', e.target.value)} data-testid="qd-first-name" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Last name</Label>
                    <Input className="h-8 text-sm" value={editDraft.last_name || ''} onChange={(e) => upd('last_name', e.target.value)} data-testid="qd-last-name" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Email</Label>
                    <Input className="h-8 text-sm" value={editDraft.email || ''} onChange={(e) => upd('email', e.target.value)} data-testid="qd-email" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Phone</Label>
                    <Input className="h-8 text-sm" value={editDraft.phone || ''} onChange={(e) => upd('phone', e.target.value)} data-testid="qd-phone" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Address</Label>
                    <Input className="h-8 text-sm" value={editDraft.address || ''} onChange={(e) => upd('address', e.target.value)} data-testid="qd-address" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">City</Label>
                    <Input className="h-8 text-sm" value={editDraft.city || ''} onChange={(e) => upd('city', e.target.value)} data-testid="qd-city" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Postcode</Label>
                    <Input className="h-8 text-sm" value={editDraft.postcode || ''} onChange={(e) => upd('postcode', e.target.value)} data-testid="qd-postcode" />
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
                <div className="text-xs font-semibold text-slate-500 uppercase">Hardware — edit as needed</div>
                <div className="grid grid-cols-[72px_1fr_1fr_52px] gap-2 items-center">
                  <span />
                  <span className="text-[10px] text-slate-400 uppercase">Make</span>
                  <span className="text-[10px] text-slate-400 uppercase">Model</span>
                  <span className="text-[10px] text-slate-400 uppercase">Qty</span>
                </div>
                {[['Panels', 'panel'], ['Inverter', 'inverter'], ['Battery', 'battery'], ['EV Charger', 'ev_charger']].map(([label, p]) => (
                  <div key={p} className="grid grid-cols-[72px_1fr_1fr_52px] gap-2 items-center">
                    <span className="text-xs text-slate-600">{label}</span>
                    <Input className="h-8 text-sm" value={editDraft[`${p}_manufacturer`] || ''} onChange={(e) => upd(`${p}_manufacturer`, e.target.value)} data-testid={`qd-${p}-manufacturer`} />
                    <Input className="h-8 text-sm" value={editDraft[`${p}_model`] || ''} onChange={(e) => upd(`${p}_model`, e.target.value)} data-testid={`qd-${p}-model`} />
                    <Input type="number" min="0" className="h-8 text-sm px-1" value={editDraft[`${p}_quantity`] || 0} onChange={(e) => upd(`${p}_quantity`, e.target.value === '' ? 0 : Number(e.target.value))} data-testid={`qd-${p}-qty`} />
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div>
                    <Label className="text-xs text-slate-500">System size (kWp)</Label>
                    <Input type="number" step="0.01" min="0" className="h-8 text-sm" value={editDraft.system_size || 0} onChange={(e) => upd('system_size', e.target.value === '' ? 0 : Number(e.target.value))} data-testid="qd-system-size" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Total cost (£)</Label>
                    <Input type="number" step="0.01" min="0" className="h-8 text-sm" value={editDraft.system_total_cost || 0} onChange={(e) => upd('system_total_cost', e.target.value === '' ? 0 : Number(e.target.value))} data-testid="qd-total-cost" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Additional works <span className="text-slate-400">(added to customer notes)</span></Label>
                  <Textarea rows={3} className="text-sm" value={editDraft.additional_extras || ''} onChange={(e) => upd('additional_extras', e.target.value)} placeholder="e.g. Scaffolding, bird protection, additional circuits…" data-testid="qd-additional-works" />
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Quote line items</div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {preview.line_items.map((li, i) => (
                    <div key={i} className="flex justify-between gap-3 text-xs text-slate-600">
                      <span className="truncate">{li.quantity ? `${li.quantity} × ` : ''}{li.description}</span>
                      <span className="shrink-0">{fmtGBP(li.line_amount)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="xero-quote-cancel-btn">Cancel</Button>
                <Button type="button" onClick={applyToForm} data-testid="xero-quote-apply-btn">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Apply to form
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default XeroQuoteImportCard;
